import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getParentPath, convertRGBToHex, LayerType, GetSeedsDict } from 'utils/Global';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import PlateService from 'business/services/PlateService';
import { IngredientReportOptionsDTO, fonts, pageSize } from './ReportsConstants';
import { WellNavigationDetails } from 'components/Experiment/constants';
import {
  createPdfWithPopupBlockerHandler,
  estimateCharsPerLine,
  getFieldRuns,
  getScreenNameLineRuns,
  getScreenNamesFromLayers,
  IngReportTableRow,
  IngReportTableRows,
  INGREDIENT_NAME_SUFFIX,
  WELL_SOLUTION_LABEL,
  INGREDIENT_REPORT_INFO_FONT_SIZE,
  INGREDIENT_REPORT_INFO_MARGIN,
} from './utils';
import {
  FragmentLayerDTO,
  ProteinLayerDTO,
  ScreenLayerDTO,
  SeedLayerDetailsDTO,
} from 'business/models/Models';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

const getInfoFieldRuns = (fields: { label: string; value: any }[]) =>
  getFieldRuns(fields, '   ');

export async function generateIngredientReportPDF(
  options: IngredientReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
  dropNumber: number,
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  pdfMake.fonts = fonts;

  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Ingredient Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(options, expData, wellDetails, dropNumber),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
      font: options.fontFamily.key,
      bold:
        options.fontStyle == 'Bold' || options.fontStyle == 'Bold Italic' ? true : false,
      italics:
        options.fontStyle == 'Italic' || options.fontStyle == 'Bold Italic'
          ? true
          : false,
    },
    pageMargins: [10, 50, 10, 0],
    pageOrientation: options.rowLimit > options.columnLimit ? 'portrait' : 'landscape',
    pageSize: 'A4',
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Ingredients Report',
    saveReport,
    onPopupBlocked,
  );
}

export async function CreateContent(
  options: IngredientReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
  dropNumber: number,
) {
  const content = [];
  if (options.fitToPage === true) {
    options.columnLimit = expData.containerCols;
    options.rowLimit = expData.containerRows;
  }
  const wellInfo = wellDetails[0];
  const plateInfo: any = expData.plateDto.find(
    (plate) => plate.plateId === wellInfo.plateID,
  );

  const treeService = new TreeService();
  const expParents = await treeService.getDocumentParentNodes(
    expData.experimentId,
    ETreeNodeTypes.Experiment,
    true,
  );
  const expPath = expParents ? getParentPath(expParents) : '';
  const screenNames = getScreenNamesFromLayers(expData.layers);
  const orientation = options.rowLimit > options.columnLimit ? 'portrait' : 'landscape';
  const contentWidth =
    (orientation === 'portrait' ? pageSize.a4.width : pageSize.a4.height) -
    INGREDIENT_REPORT_INFO_MARGIN * 2;
  const maxLineChars = estimateCharsPerLine(
    contentWidth,
    INGREDIENT_REPORT_INFO_FONT_SIZE,
  );
  const screenNameRuns = getScreenNameLineRuns(screenNames, maxLineChars);
  let _dropNumber = 0;
  let pageBreak;

  if (options.isScreenExperiment) {
    _dropNumber = null;
    const data = await getWellsTable(
      options,
      _dropNumber,
      expData,
      plateInfo.plateId,
      wellInfo,
    );

    if (data.length > 0) {
      content.push({
        text: [
          ...getInfoFieldRuns([
            { label: 'Experiment: ', value: expPath },
            { label: 'Plate ID: ', value: plateInfo.plateId },
            { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
          ]),
          { text: '\n' },
          ...screenNameRuns,
        ],
        alignment: 'center',
        pageBreak: pageBreak,
      });
      content.push(await designTableData(options, data, expData));
      pageBreak = 'before';
    } else {
      content.push({
        text: 'The document does not contain any pages.',
        alignment: 'center',
        fontSize: 12,
        color: 'grey',
      });
    }
  } else {
    for (const drop of options.drops) {
      if (drop.checked) {
        _dropNumber = dropNumber;
        const data = await getWellsTable(
          options,
          drop.dropNumber,
          expData,
          plateInfo.plateId,
          wellInfo,
        );

        if (data.length > 0) {
          content.push({
            text: [
              ...getInfoFieldRuns([
                { label: 'Experiment: ', value: expPath },
                { label: 'Plate ID: ', value: plateInfo.plateId },
                { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
                { label: 'Drop Number: ', value: drop.dropNumber },
              ]),
              { text: '\n' },
              ...screenNameRuns,
            ],
            alignment: 'center',
            pageBreak: pageBreak,
          });
          content.push(await designTableData(options, data, expData));
          pageBreak = 'before';
        } else {
          content.push({
            text: 'The document does not contain any pages.',
            alignment: 'center',
            fontSize: 12,
            color: 'grey',
          });
        }
      }
    }
  }

  content.push('\n');
  content.push('\n');
  return content;
}

export async function designTableData(
  options: IngredientReportOptionsDTO,
  data: any,
  expData: ExperimentDtoClient,
) {
  const columnLimit = options.columnLimit;
  const rowLimit = options.rowLimit;
  const rows: any[] = [];
  let columns: any[] = [];
  let rowCount = 1;
  let rowStart = 0;
  let columnStart = 1;
  let rowIndex = 0;
  while (rowIndex < expData.containerRows) {
    for (let i = rowStart; i < rowLimit + rowStart; i++) {
      let columnCount = 1;

      for (let j = columnStart; j < columnLimit + columnStart; j++) {
        if (columnCount === 1) {
          if (i < data.length) {
            columns.push({
              index: '',
              data: data[i].columns[0],
            });
          }
        }
        if (i < data.length) {
          columns.push({
            index: j,
            data: data[i].columns[j],
          });
        }
        if (columnCount === columnLimit || j === expData.containerCols) {
          if (columns.length > 0) {
            const targetLength = columnLimit + 1;
            for (let pad = columns.length; pad < targetLength; pad++) {
              columns.push({ index: '', data: [{}] });
            }
          }
          rows.push(columns);
          columns = [];
          columnCount = 0;
        }
        columnCount++;
        if (j === expData.containerCols) {
          break;
        }
      }

      if (rowCount === rowLimit) {
        columnStart = columnStart + columnLimit;
        if (i >= expData.containerRows && columnStart > expData.containerCols) {
          rowIndex = rowStart + rowLimit;
          break;
        }
        if (columnStart > expData.containerCols) {
          columnStart = 1;
          rowStart = rowStart + rowLimit;
          rowIndex = rowStart;
          rowCount = 0;
        } else {
          rowStart = rowIndex;
          rowCount = 0;
        }
      }
      rowCount++;
      if (i === expData.containerRows && columnStart > expData.containerCols) {
        rowIndex = rowStart + rowLimit;
        break;
      }
    }
  }

  let loopCount = 0;
  while (loopCount < rows.length) {
    if (rows[loopCount].length === 0) {
      rows.splice(loopCount, 1);
    } else {
      ++loopCount;
    }
  }

  let lastRows: any[] = [];
  const rowTable: any[] = [];

  let rowNumber = 0;

  for (const row of rows) {
    rowNumber++;
    if (row.length > 0) {
      if (lastRows.length === 0) {
        let columnTop: any[] = [];
        row.forEach((col) => {
          columnTop.push({
            data: [
              {
                text: col.index,
                alignment: 'center',
                border: [false, false, false, false],
              },
            ],
          });
        });

        lastRows.push(columnTop);
        columnTop = [];
      }

      lastRows.push(row);
      if (lastRows.length === options.rowLimit + 1) {
        let pageBreak = 'after';
        if (rowNumber == rows.length) {
          pageBreak = '';
        }

        rowTable.push(await getFinalTable(lastRows, pageBreak, options));
        lastRows = [];
      }
    }
  }

  if (lastRows.length > 0) {
    rowTable.push(await getFinalTable(lastRows, '', options));
  }

  return rowTable;
}

async function getFinalTable(
  rows: any[],
  pageBreak: string,
  options: IngredientReportOptionsDTO,
) {
  const rows1: any[] = [];
  let tableColumns: any[] = [];

  rows.forEach((row) => {
    if (row.length > 0) {
      row.forEach((col) => {
        if (col.index === '') {
          const tempData = { ...col.data[0] };
          tableColumns.push(tempData);
        } else {
          tableColumns.push(col.data[0]);
        }
      });

      rows1.push(tableColumns);
      tableColumns = [];
    }
  });

  const numberOfColumns = rows1[0].length - 1;
  const widths = [];

  widths.push('1%');
  const singleColumnWidth = 99 / options.columnLimit;
  for (let i = 0; i < numberOfColumns; i++) {
    widths.push(singleColumnWidth.toString() + '%');
  }

  return {
    pageBreak: pageBreak,
    table: {
      widths: widths,
      headerRows: rows1.length,
      body: rows1,
    },
    layout: TABLE_LAYOUT,
  };
}

export const buildDropTableRows = (
  drop: any,
  expData: ExperimentDtoClient,
  options: IngredientReportOptionsDTO,
  columnCount: number,
) => {
  const rows: IngReportTableRows = [];
  const categories: { name: string; volume: number | null }[] = [];

  const dropLayerIds = (() => {
    const plate = expData.plateDto?.find((p) => p.plateId === drop.plateId);
    if (!plate) {
      return [];
    }
    const well = plate.ingredients?.wellList?.find(
      (w) => w.wellNumber === drop.wellNumber,
    );
    if (!well) {
      return [];
    }
    const drp = well.dropList?.find((d) => d.dropNumber === drop.dropNumber);
    return drp?.dropLayerList?.map((dl) => dl.layerId) ?? [];
  })();

  const fragmentLayer = expData.layers.find(
    (layer) =>
      layer.layerDetail instanceof FragmentLayerDTO && dropLayerIds.includes(layer.id),
  );
  const seedLayer = expData.layers.find(
    (layer) =>
      layer.layerDetail instanceof SeedLayerDetailsDTO && dropLayerIds.includes(layer.id),
  );

  if (
    options.showProteinName &&
    expData.layers.some((layer) => layer.layerDetail instanceof ProteinLayerDTO)
  ) {
    categories.push({
      name:
        (drop.proteinFormulationName || 'Protein Name') + INGREDIENT_NAME_SUFFIX.Protein,
      volume: drop.dropProteinVolume ?? null,
    });
    categories.push({
      name: WELL_SOLUTION_LABEL + INGREDIENT_NAME_SUFFIX[WELL_SOLUTION_LABEL],
      volume: drop.dropWellVolume ?? null,
    });
  }

  if (options.showFragmentName && fragmentLayer) {
    categories.push({
      name:
        ((fragmentLayer.layerDetail as FragmentLayerDTO)?.fragmentScreen?.treeNode
          ?.name || fragmentLayer.name) + INGREDIENT_NAME_SUFFIX.Fragment,
      volume: drop.dropFragment ?? null,
    });
  }
  if (options.showSeedName && seedLayer) {
    const seeds = GetSeedsDict();
    const seedName = seeds[drop.dropSeedID]?.name || seedLayer.name;
    categories.push({
      name: seedName + INGREDIENT_NAME_SUFFIX.Seed,
      volume: drop.dropSeedVolume ?? null,
    });
  }

  if (options.showAdditiveName) {
    if (drop.hasDropScreenLayer) {
      const dropScreenLayer = expData.layers.find(
        (layer) => layer.type === LayerType.Screen && layer.targetDrop == drop.dropNumber,
      );
      const screenName =
        (dropScreenLayer?.layerDetail as ScreenLayerDTO)?.screenLot?.experiment
          ?.expName ?? '';
      categories.push({
        name: screenName + INGREDIENT_NAME_SUFFIX.Additive,
        volume: drop.dropScreenLayerVolume ?? null,
      });
    }

    const plate = expData.plateDto?.find((p) => p.plateId === drop.plateId);
    if (plate) {
      const well = plate.ingredients.wellList.find(
        (w) => w.wellNumber === drop.wellNumber,
      );
      if (well) {
        const drp = well.dropList.find((d) => d.dropNumber === drop.dropNumber);
        if (drp && drp.dropLayerList) {
          drp.dropLayerList.forEach((dl) => {
            const matchingLayer = expData.layers.find((l) => l.id === dl.layerId);
            if (
              matchingLayer &&
              !(
                matchingLayer.layerDetail instanceof ProteinLayerDTO ||
                matchingLayer.layerDetail instanceof FragmentLayerDTO ||
                matchingLayer.layerDetail instanceof SeedLayerDetailsDTO ||
                matchingLayer.layerDetail instanceof ScreenLayerDTO
              )
            ) {
              categories.push({
                name: matchingLayer.name + INGREDIENT_NAME_SUFFIX.Additive,
                volume: dl.volume ?? null,
              });
            }
          });
        }
      }
    }
  }

  const buildHeader = () => {
    if (columnCount === 1) {
      return [
        {
          text: 'Drop / Vol (μL)',
          bold: true,
          fillColor: '#808080',
          color: '#FFFFFF',
          alignment: 'center',
          border: [true, true, true, true],
        },
      ];
    }
    const headerRow: IngReportTableRow = [
      {
        text: 'Drop',
        bold: true,
        fillColor: '#808080',
        color: '#FFFFFF',
        alignment: 'left',
        colSpan: columnCount - 1,
        border: [true, true, true, true],
      },
    ];
    for (let i = 1; i < columnCount - 1; i++) {
      headerRow.push({});
    }
    headerRow.push({
      text: 'Vol (μL)',
      bold: true,
      fillColor: '#808080',
      color: '#FFFFFF',
      alignment: 'center',
      border: [true, true, true, true],
    });
    return headerRow;
  };

  const buildRow = (name: string, volumeText: string) => {
    if (columnCount === 1) {
      return [
        {
          text: `${name}: ${volumeText}`,
          alignment: 'left',
          border: [true, true, true, true],
        },
      ];
    }
    const dataRow: IngReportTableRow = [
      {
        text: name,
        alignment: 'left',
        colSpan: columnCount - 1,
        border: [true, true, true, true],
      },
    ];
    for (let i = 1; i < columnCount - 1; i++) {
      dataRow.push({});
    }
    dataRow.push({
      text: volumeText,
      alignment: 'center',
      border: [true, true, true, true],
    });
    return dataRow;
  };

  rows.push(buildHeader());

  if (categories.length === 0) {
    rows.push(buildRow('-', '-'));
  } else {
    categories.forEach((category) => {
      const volumeValue = category.volume ? Number(category.volume.toFixed(3)) : '0.00';
      rows.push(buildRow(category.name, `${volumeValue}µL`));
    });

    const wellSolCatIdx = categories.findIndex(
      (c) => c.name === WELL_SOLUTION_LABEL + INGREDIENT_NAME_SUFFIX[WELL_SOLUTION_LABEL],
    );
    if (wellSolCatIdx > 0) {
      const prevRow = rows[wellSolCatIdx];
      const wellSolRow = rows[wellSolCatIdx + 1];
      prevRow.forEach((cell) => {
        if (cell && cell.border) {
          cell.border[3] = false;
        }
      });
      wellSolRow.forEach((cell) => {
        if (cell && cell.border) {
          cell.border[1] = false;
        }
      });
    }
  }

  return rows;
};

export async function getWellsTable(
  options: IngredientReportOptionsDTO,
  dropNumber: number,
  expData: ExperimentDtoClient,
  plateId: number,
  wellInfo: any,
) {
  const rows: any = [];
  let columns: any = [];
  const wellData = await getWellData(expData, wellInfo, dropNumber);
  const plateService = new PlateService();
  const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(plateId);
  const ignoredScoreIcon = await getIgnoredScoreIcon();

  let width;
  let rowHeight;
  let tableHeight;
  if (options.rowLimit > options.columnLimit) {
    tableHeight = 650;
    width = 500 / options.columnLimit;
    rowHeight = 800 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }
  } else {
    width = 760 / options.columnLimit;
    rowHeight = 500 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }

    tableHeight = 420;
  }

  let dropImageCount = 0;
  const columnLoopCount = expData.containerCols + 1;
  for (let i = 0; i < expData.containerRows; i++) {
    for (let j = 0; j < columnLoopCount; j++) {
      if (j === 0) {
        columns.push([
          {
            width: 20,
            text: String.fromCharCode('A'.charCodeAt(0) + i),
            alignment: 'center',
            margin: [0, tableHeight / options.rowLimit / 2 - 10, 0, 0],
            border: [false, false, false, false],
          },
        ]);
      } else {
        const wellScores: any[] = [];
        if (plateScores.length > 0) {
          const inspectionSet: any = expData.inspections.find(
            (insp) => insp.id == expData.expSelectedStates.inspectionSetId,
          );
          const imageBatchId: number = inspectionSet.groupedInspections.find(
            (gi) => gi.plateId == plateId,
          ).imageBatchId;
          plateScores.forEach((scores) => {
            scores.forEach((wellScore) => {
              if (
                wellScore.wellNumber == wellData[dropImageCount].wellNumber &&
                wellScore.dropNumber == dropNumber &&
                wellScore.scoreBatchId == imageBatchId
              ) {
                wellScores.push(wellScore);
              }
            });
          });
        }
        const scoresBody: any[] = [];
        const scoreTextArray: any[] = [];
        scoresBody.push({
          text: '',
          border: [false, false, false, false],
        });

        scoreTextArray.push(' ');
        const scoreEntries: { wellScore: any; scoreText: string }[] = [];
        wellScores.forEach((wellScore) => {
          let scoreText = '';

          if (wellScore.isAutoScore) {
            if (wellScore.autoScoreValue == 1) {
              scoreText = '+';
            } else if (wellScore.autoScoreValue == 0) {
              scoreText = '-';
            } else if (wellScore.autoScoreValue == null) {
              scoreText = ' ';
            } else {
              scoreText = wellScore.autoScoreValue.toFixed(2);
            }
          } else {
            scoreText = wellScore.hotKey;
          }
          scoreEntries.push({ wellScore, scoreText });
        });

        let columnCount = 0;
        const ingredientDataOptions: any = [];
        if (options.concentration) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'C' : 'Conc',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pHVolumes) {
          columnCount = columnCount + 2;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'L' : 'L vol',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });

          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'H' : 'H vol',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pH) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: 'pH',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
            margin: [0, 0, 0, 0],
            padding: [0, 0, 0, 0],
          });
        }

        const dropTableRows = options.showDropVolumes
          ? buildDropTableRows(wellData[dropImageCount], expData, options, columnCount)
          : [];

        let rowCount = 0;
        const headerRowCount = 1;
        const wellVolumeRowCount = 1;

        rowCount = rowCount + headerRowCount + wellVolumeRowCount;
        if (options.showProteinName && !options.showDropVolumes) {
          rowCount = rowCount + 1;
        }
        rowCount = rowCount + dropTableRows.length;
        if (options.showWellVolumes) {
          rowCount = rowCount + 1;
        }
        if (wellData[dropImageCount].wellIngredientList.length > 0) {
          if (options.showColumnNameHeading) {
            rowCount = rowCount + 1;
          }

          for (let i = 0; i < options.maxIngredients; i++) {
            rowCount = rowCount + 2;
          }
        } else {
          rowCount = rowCount + 1;

          for (let i = 0; i < options.maxIngredients; i++) {
            for (let j = 0; j < 2; j++) {
              rowCount = rowCount + 1;
            }
          }
        }

        //font size calculation
        const cellRowHeight = tableHeight / options.rowLimit / rowCount;

        const wellWidth =
          options.rowLimit > options.columnLimit
            ? 500 / options.columnLimit
            : 760 / options.columnLimit;
        const avgColumnWidth = wellWidth / columnCount;

        const fontSizeByHeight = cellRowHeight * 0.5;

        const fontSizeByWidth = avgColumnWidth / 4 / 0.6;

        let fontSize = Math.min(fontSizeByHeight, fontSizeByWidth);

        const MIN_FONT_SIZE = 2;
        const MAX_FONT_SIZE = options.fontSize;

        fontSize = Math.max(MIN_FONT_SIZE, Math.min(fontSize, MAX_FONT_SIZE));

        const totalWells = options.columnLimit * options.rowLimit;
        if (totalWells >= 96) {
          let densityScale = 1.0;
          if (totalWells >= 384) {
            densityScale = Math.sqrt(96 / totalWells);
          } else if (totalWells > 96) {
            densityScale = 96 / totalWells;
          }
          fontSize = fontSize * densityScale;
        }

        fontSize = Math.max(0.5, fontSize);

        const scoreCellSize = Math.max(fontSize * 1.2, fontSize * 4 * 0.65, 2);
        scoreEntries.forEach(({ wellScore, scoreText }) => {
          if (wellScore.isAutoScoreIgnored) {
            scoresBody.push(
              buildIgnoredScoreImageCell(
                wellScore.colorRGB,
                scoreText,
                scoreCellSize,
                ignoredScoreIcon,
              ),
            );
          } else {
            scoresBody.push({
              text: scoreText,
              alignment: 'center',
              fillColor: convertRGBToHex(wellScore.colorRGB),
              color: 'white',
              border: [false, false, true, false],
            });
          }
          scoreTextArray.push(scoreText);
        });
        const scoresWidth: any[] = [];
        for (let i = 0; i < scoresBody.length; i++) {
          if (i == 0) {
            scoresWidth.push('*');
          } else {
            scoresWidth.push(scoreCellSize);
          }
        }

        const topMargin = Math.max(0, (scoreCellSize - fontSize * 1.2) / 2);
        scoresBody.forEach((cell) => {
          if (cell && cell.text) {
            cell.margin = [0, topMargin, 0, 0];
          }
        });

        const singleColumnMaxWidth =
          options.columnLimit > options.rowLimit
            ? 750 / options.columnLimit
            : 550 / options.columnLimit;

        const maxChars = Math.floor(singleColumnMaxWidth / (fontSize * 0.6));

        const body: any[] = [];

        const headerArray: any[] = [];
        headerArray.push({
          columns: [
            {
              width: '70%',
              text:
                'Plate ID ' +
                wellData[dropImageCount].plateId +
                ', ' +
                wellData[dropImageCount].wellName +
                (wellData[dropImageCount].dropNumber
                  ? '-' + wellData[dropImageCount].dropNumber
                  : ''),
              bold: true,
            },
            {
              width: '*',
              margin: [getScoreTableLeftMagin(options), 0, 0, 0],
              table: {
                widths: scoresWidth,
                body: [scoresBody],
                heights: scoreCellSize,
              },
              layout: {
                hLineWidth: function hLineWidth(i) {
                  return 0.1;
                },
                vLineWidth: function vLineWidth(i) {
                  return 0.1;
                },
                paddingLeft: function (i, node) {
                  return 0;
                },
                paddingRight: function (i, node) {
                  return 0;
                },
                paddingTop: function (i, node) {
                  return 0;
                },
                paddingBottom: function (i, node) {
                  return 0;
                },
              },
            },
          ],
          colSpan: columnCount,
          border: [true, false, true, false],
        });

        for (let i = 1; i < columnCount; i++) {
          headerArray.push({});
        }
        body.push(headerArray);

        {
          const wellVolumeArray: any[] = [];
          wellVolumeArray.push({
            text: wellData[dropImageCount].wellScreenLayerName
              ? wellData[dropImageCount].wellScreenLayerName
              : ' ',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            wellVolumeArray.push({});
          }

          body.push(wellVolumeArray);
        }

        if (options.showProteinName && !options.showDropVolumes) {
          const proteinNameArray: any[] = [];
          const proteinName = wellData[dropImageCount].proteinFormulationName || '';

          const prefix = 'Protein: ';
          const maxProteinNameLength = Math.max(1, maxChars - prefix.length / 2);
          const truncatedProteinName =
            proteinName.length > maxProteinNameLength
              ? proteinName.slice(0, maxProteinNameLength) + '...'
              : proteinName;

          proteinNameArray.push({
            text: proteinName ? prefix + truncatedProteinName : '',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            proteinNameArray.push({});
          }

          body.push(proteinNameArray);
        }

        if (options.showDropVolumes) {
          dropTableRows.forEach((row) => {
            body.push(row);
          });
        }

        if (options.showWellVolumes) {
          const wellVolumeArray: any[] = [];
          wellVolumeArray.push({
            text:
              'Well: ' +
              (wellData[dropImageCount].wellVolume
                ? Number(wellData[dropImageCount].wellVolume.toFixed(3))
                : '0.00') +
              'µL / ' +
              (wellData[dropImageCount].wellWaterVolume
                ? Number(wellData[dropImageCount].wellWaterVolume.toFixed(3))
                : '0.00') +
              'µL H2O',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            wellVolumeArray.push({});
          }

          body.push(wellVolumeArray);
        }

        if (wellData[dropImageCount].wellIngredientList.length > 0) {
          if (options.showColumnNameHeading) {
            body.push(ingredientDataOptions);
          }

          let highlightRow = false;

          for (let i = 0; i < options.maxIngredients; i++) {
            const ingredient = wellData[dropImageCount].wellIngredientList[i];
            const ingLongName = ingredient?.ingredientStockIngredient.longName;
            const ingShortName = ingredient?.ingredientStockIngredient.shortName;
            const ingConcentration = ingredient?.concentration;
            const ingConcUnit = ingredient?.concentrationUnit;
            const ingpH = ingredient?.pH;
            const lowVolume = ingredient?.isBuffer
              ? ingredient?.lowPHVolume
              : ingredient?.volume;
            const highVolume = ingredient?.highPHVolume;

            let fillColor;
            if (highlightRow) {
              fillColor = '#D3D3D3';
            } else {
              fillColor = '#FFFFFF';
            }
            const ingNameArray: any = [];
            ingNameArray.push({
              text: ingredient
                ? options.ingredientNamePreference == 'longName'
                  ? ingLongName.length > maxChars
                    ? ingLongName.slice(0, maxChars) + '...'
                    : ingLongName
                  : ingShortName
                : ' ',
              colSpan: columnCount,
              alignment: 'left',
              fillColor: fillColor,
              border: [true, true, true, true],
            });

            for (let i = 1; i < columnCount; i++) {
              ingNameArray.push({});
            }

            body.push(ingNameArray);

            const ingDataArray: any = [];

            if (options.concentration) {
              ingDataArray.push({
                text:
                  ingConcentration != null
                    ? Number(ingConcentration?.toFixed(3)) + ingConcUnit
                    : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pHVolumes) {
              ingDataArray.push({
                text: lowVolume != null ? `${Number(lowVolume.toFixed(1))}µL` : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });

              ingDataArray.push({
                text: highVolume != null ? Number(highVolume?.toFixed(1)) + 'µL' : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pH) {
              ingDataArray.push({
                text: ingpH ? Number(ingpH?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }

            body.push(ingDataArray);
            highlightRow = !highlightRow;
          }
        } else {
          const fillColor = '#FFFFFF';

          const headerArray: any = [];
          headerArray.push({
            text: 'header',
            colSpan: columnCount,
            alignment: 'center',
            fillColor: fillColor,
            color: fillColor,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            headerArray.push({});
          }
          body.push(headerArray);

          for (let i = 0; i < options.maxIngredients; i++) {
            for (let j = 0; j < 2; j++) {
              const ingArray: any[] = [];
              ingArray.push({
                text: 'header',
                colSpan: columnCount,
                alignment: 'center',
                fillColor: fillColor,
                color: fillColor,
                border: [true, false, true, false],
              });

              for (let i = 1; i < columnCount; i++) {
                ingArray.push({});
              }
              body.push(ingArray);
            }
          }
        }

        const columnWidths = [];

        let concentrationWidth = 0;
        let otherColumnWidth = 0;

        if (columnCount === 1) {
          concentrationWidth = 100;
          if (options.pH) {
            concentrationWidth = 0;
            otherColumnWidth = 100;
          }
        } else if (columnCount === 2) {
          concentrationWidth = 60;
          otherColumnWidth = 40;
          if (options.pHVolumes) {
            otherColumnWidth = 50;
            concentrationWidth = 0;
          }
        } else if (columnCount === 3) {
          if (options.concentration) {
            concentrationWidth = 50;
            otherColumnWidth = 25;
          } else {
            otherColumnWidth = 33.33;
          }
        } else if (columnCount === 4) {
          concentrationWidth = 40;
          otherColumnWidth = 20;
        }

        if (options.concentration) {
          columnWidths.push(concentrationWidth.toString() + '%');
        }
        if (options.pHVolumes) {
          columnWidths.push(otherColumnWidth.toString() + '%');
          columnWidths.push(otherColumnWidth.toString() + '%');
        }
        if (options.pH) {
          columnWidths.push(otherColumnWidth.toString() + '%');
        }

        columns.push([
          {
            stack: [
              {
                fontSize: fontSize,
                margin: [-5, -2, -5, -2],
                table: {
                  heights: tableHeight / options.rowLimit / rowCount,
                  widths: columnWidths,
                  body: body,
                },
                layout: {
                  hLineWidth: function hLineWidth(i) {
                    return 0.1;
                  },
                  vLineWidth: function vLineWidth(i, node) {
                    if (i === 0 || i === node.table.widths.length) {
                      return 1;
                    }
                    return 0.1;
                  },
                  vLineColor: function vLineColor(i, node) {
                    if (i === 0 || i === node.table.widths.length) {
                      return;
                    }
                    return '#555555';
                  },
                  paddingLeft: function (i, node) {
                    return 2;
                  },
                  paddingRight: function (i, node) {
                    return 0;
                  },
                  paddingTop: function (i, node) {
                    const text = node.table.body[i][0]._inlines;
                    if (text && text.length > 0) {
                      const textHeight = text[0].height;
                      const margin = (node.table.heights - textHeight) / 2;
                      node.table.body[i].forEach((cell) => {
                        if (cell._margin) {
                          cell._margin[1] = margin;
                        } else {
                          cell._margin = [0, margin, 0, 0];
                        }
                      });
                    }
                    return 0;
                  },
                  paddingBottom: function (i, node) {
                    return 0;
                  },
                },
              },
            ],
          },
        ]);
        dropImageCount++;
      }
    }
    rows.push({
      alignment: 'center',
      columns: columns,
    });
    columns = [];
  }
  return rows;
}

async function getWellData(
  expData: ExperimentDtoClient,
  wellInfo: any,
  dropNumber: number,
) {
  const plateData = expData.plateDto.find((plt) => plt.plateId == wellInfo.plateID);
  const wellList: any[] = [];

  plateData.ingredients.wellList.forEach((well) => {
    const wellData: any = {};
    wellData.wellName = well.rowLetter + well.columnNumber.toString();
    wellData.wellNumber = well.wellNumber;
    wellData.plateId = plateData.plateId;
    wellData.wellVolume = well.wellVolume;
    wellData.dropNumber = dropNumber > 0 ? dropNumber : null;
    wellData.wellWaterVolume = well.wellWaterVolume;

    let dropData: any = {};
    dropData = well.dropList.find((drp) => drp.dropNumber == dropNumber);

    wellData.dropProteinVolume = dropData?.dropProteinVolume;
    wellData.dropWellVolume = dropData?.dropWellVolume;
    wellData.dropSeedVolume = dropData?.dropSeedVolume;
    wellData.dropSeedID = dropData?.seedIDs?.[0];
    wellData.dropVolume = dropData?.volume;
    wellData.proteinFormulationName =
      plateData.ingredients.proteinFormulation[dropData?.proteinFormulationID]
        ?.proteinInfo.name || undefined;
    wellData.dropFragment = dropData?.dropFragmentVolume;
    wellData.wellIngredientList = [];

    well.wellLayerList.forEach((wellLyr) => {
      wellLyr.wellLayerIngredientList.forEach((ing) => {
        wellData.wellIngredientList.push(ing);
      });
    });

    const wellScreenLayer = expData.layers.find(
      (l) =>
        l.type === LayerType.Screen &&
        well.wellLayerList.map((wl) => wl.layerId).includes(l.id),
    );

    wellData.wellScreenLayerName = wellScreenLayer ? wellScreenLayer.name : '';

    const screenLayer = expData.layers.find((layer) => layer.type === LayerType.Screen);
    const dropScreenLayer = expData.layers.find(
      (layer) => layer.type === LayerType.Screen && layer.targetDrop === dropNumber,
    );
    const matchingDropScreenLayer = dropData?.dropLayerList?.find(
      (l) => l.layerId === dropScreenLayer?.id,
    );
    wellData.screenLayerVolume = screenLayer?.volume;
    wellData.hasDropScreenLayer = !!matchingDropScreenLayer;
    wellData.dropScreenLayerVolume = dropScreenLayer?.volume;

    wellList.push(wellData);
  });

  return wellList;
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
          alignment: 'right',
        },
      ],
      margin: [20, 5, 20, 20],
    },
  ];
}

const getScoreTableLeftMagin = (options: IngredientReportOptionsDTO) => {
  let tableWidth = 0;
  let columnWidth = 0;
  if (options.rowLimit > options.columnLimit) {
    tableWidth = 420;
    tableWidth = tableWidth - (1 / 100) * tableWidth;
    columnWidth = tableWidth / options.columnLimit;
  } else {
    tableWidth = 500;
    tableWidth = tableWidth - (1 / 100) * tableWidth;
    columnWidth = tableWidth / options.columnLimit;
  }

  return -columnWidth;
};

let ignoredScoreIcon: HTMLImageElement | null = null;

async function getIgnoredScoreIcon(): Promise<HTMLImageElement> {
  if (!ignoredScoreIcon) {
    ignoredScoreIcon = RMSvgIconImage({ name: 'EyeSlash', fill: '#dc0100' });
    await new Promise<void>((resolve) => {
      if (ignoredScoreIcon!.complete && ignoredScoreIcon!.naturalWidth > 0) {
        resolve();
        return;
      }

      ignoredScoreIcon!.onload = () => resolve();
      ignoredScoreIcon!.onerror = () => resolve();
    });
  }

  return ignoredScoreIcon;
}

function buildIgnoredScoreImageCell(
  colorRGB: number,
  scoreText: string,
  displaySize: number,
  icon: HTMLImageElement,
) {
  const size = Math.max(2, Math.round(displaySize));
  const renderSize = Math.max(size * 4, 64);
  const canvas = document.createElement('canvas');
  canvas.width = renderSize;
  canvas.height = renderSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      text: scoreText,
      alignment: 'center',
      fillColor: convertRGBToHex(colorRGB),
      color: 'white',
      border: [false, false, true, false],
    };
  }

  ctx.fillStyle = convertRGBToHex(colorRGB);
  ctx.fillRect(0, 0, renderSize, renderSize);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `bold ${Math.max(10, renderSize * 0.45)}px Roboto`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(scoreText, renderSize / 2, renderSize / 2);
  ctx.drawImage(icon, 0, 0, renderSize, renderSize);

  return {
    image: canvas.toDataURL('image/png'),
    width: size,
    height: size,
    alignment: 'center',
    border: [false, false, true, false],
  };
}
